import { useUser, useSession } from '@clerk/vue'
import { computed } from 'vue'

export function useClerkUser() {
  const { user, isLoaded, isSignedIn } = useUser()
  const { session } = useSession()

  const role = computed(() => (user.value?.publicMetadata?.role as string) ?? '')
  const isAdmin = computed(() => role.value === 'admin')
  const isUser = computed(() => !!isSignedIn.value)

  /** Capitalizes just the first letter of a word, leaving the rest as-is
   * (so "McDonald" isn't mangled) -- display-level fix, not just an
   * input-time one, so it corrects already-lowercase existing accounts
   * too, not just names typed in going forward. */
  function capitalizeWord(w: string): string {
    return w.length ? w[0].toUpperCase() + w.slice(1) : w
  }
  const displayName = computed(() => {
    if (!user.value) return ''
    const name = `${capitalizeWord(user.value.firstName ?? '')} ${capitalizeWord(user.value.lastName ?? '')}`.trim()
    return name || user.value.emailAddresses?.[0]?.emailAddress || ''
  })

  const email = computed(() => user.value?.emailAddresses?.[0]?.emailAddress ?? '')

  const initials = computed(() =>
    displayName.value.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2)
  )

  async function getToken() {
    // Cold-start safety: in a freshly opened tab this can run before Clerk
    // has finished loading — wait briefly for the session rather than
    // returning an empty token and being mistaken for signed-out.
    for (let i = 0; i < 40 && !session.value; i++) {
      await new Promise(r => setTimeout(r, 150))
    }
    return await session.value?.getToken() ?? ''
  }

  return {
    user,
    isLoaded,
    isSignedIn,
    role,
    isAdmin,
    isUser,
    displayName,
    email,
    initials,
    session,
    getToken,
  }
}